import { enableMetricGroupReleasePolicyConnections } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { pluralize } from '@gonfalon/format/pluralize';
import { TextTruncatorWithTooltip } from '@gonfalon/launchpad-experimental/TextTruncatorWithTooltip';
import { toMetricGroup } from '@gonfalon/navigator/navigators';
import { useMetricGroup } from '@gonfalon/rest-api/hooks/useMetricGroup';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { LinkButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { metricsSearchTracker } from '../analytics';
import { getMetricGroupExpandParams } from '../MetricGroups/getMetricGroupExpandParams';
import { MetricGroupTypeLabel } from '../MetricLabel';
import type { MetricGroupRep, MetricInGroupRep } from '../types';

import { trackMetricSelectionLinkToFullResourceClicked } from './analytics';

import styles from './MetricGroupDetailsPanel.module.css';

interface MetricGroupDetailsPanelProps {
  metricGroup: MetricGroupRep;
  unavailableReason?: string;
}

function getConnectionCount(item: MetricGroupRep) {
  return (item?.experimentCount ?? 0) + (item?.guardedRolloutCount ?? 0) + (item?.releasePolicyCount ?? 0);
}

function MetricGroupDetailsPanelInner({ metricGroup, unavailableReason }: MetricGroupDetailsPanelProps) {
  const projectKey = useProjectKey();
  const path = toMetricGroup({ projectKey, metricGroupKey: metricGroup.key });
  const isReleasePoliciesEnabled = enableMetricGroupReleasePolicyConnections();

  const { data: fullMetricGroup } = useMetricGroup(
    {
      projectKey,
      metricGroupKey: metricGroup.key,
      params: { expand: getMetricGroupExpandParams(isReleasePoliciesEnabled) },
    },
    { enabled: isReleasePoliciesEnabled },
  );

  const releasePolicies = isReleasePoliciesEnabled ? fullMetricGroup?.releasePolicies : undefined;

  return (
    <div className={styles.detailsPanel}>
      {/* Name */}
      <div className={styles.section}>
        <TextTruncatorWithTooltip tooltipContent={metricGroup.name} className={styles.metricName} maxLines={2}>
          {metricGroup.name}
        </TextTruncatorWithTooltip>

        <div className={styles.metricGroupData}>
          <span>{metricGroup.metrics.length ?? 0} metrics</span>
          <span>
            {getConnectionCount(metricGroup)}{' '}
            {pluralize(getConnectionCount(metricGroup) ?? 0, 'connection', 'connections')}
          </span>
        </div>

        <MetricGroupTypeLabel id={metricGroup.kind} />
      </div>

      {unavailableReason && (
        <div className={styles.unavailableReason}>
          <div className={styles.unavailableReasonTitle}>
            <Icon size="tiny" name="info-circle-outline" className={styles.unavailableReasonIcon} /> Unavailable for
            selection
          </div>
          {unavailableReason}
        </div>
      )}
      {/* Description */}
      {metricGroup.description && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Description</h4>
          <p className={styles.sectionContent}>{metricGroup.description}</p>
        </div>
      )}

      {metricGroup.metrics.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Metrics in group</h4>
          <div className={styles.sectionContent}>
            {metricGroup.metrics.map((metric: MetricInGroupRep) => (
              <span className={styles.metricInGroupItem} key={metric.key}>
                <Icon name="ruler" size="small" />
                <TextTruncatorWithTooltip tooltipContent={metric.name}>{metric.name}</TextTruncatorWithTooltip>
              </span>
            ))}
          </div>
        </div>
      )}

      {releasePolicies && releasePolicies.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Release policies</h4>
          <div className={clsx(styles.sectionContent, styles.releasePolicyContent)}>
            {releasePolicies.map((policy) => (
              <span key={policy.key} className={styles.releasePolicyItem}>
                <TextTruncatorWithTooltip tooltipContent={policy.name}>{policy.name}</TextTruncatorWithTooltip>
              </span>
            ))}
          </div>
        </div>
      )}

      <LinkButton
        href={path}
        target="_blank"
        className={styles.linkButton}
        aria-label="View metric group details"
        onPress={() => {
          const attributes = { kind: 'metricGroup' } as const;
          trackMetricSelectionLinkToFullResourceClicked(attributes);
          metricsSearchTracker.track('Link to Full Resource Clicked', attributes);
        }}
      >
        <Icon name="link-external" size="small" />
      </LinkButton>
    </div>
  );
}

export function MetricGroupDetailsPanel(props: MetricGroupDetailsPanelProps) {
  return (
    <ErrorBoundary
      owner="team-metrics"
      severity="medium"
      fallbackRender={() => (
        <div className={styles.errorContainer} style={{ padding: 'var(--lp-spacing-400)' }}>
          <span className={styles.errorMessage} style={{ color: 'var(--lp-color-text-ui-primary-base)' }}>
            An unexpected error occurred while loading this metric group's details.
          </span>
        </div>
      )}
    >
      <MetricGroupDetailsPanelInner {...props} />
    </ErrorBoundary>
  );
}
